package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.SystemContractType

/**
 * SH-112: the Polish labour code's working-time limits (Kodeks pracy, dział VI), shared by
 * auto-assign ([AutoScheduler], where they are hard rules) and the manual roster checks
 * ([LabourCodeCheck], where they are soft warnings), so both paths agree.
 *
 * Times are minutes since the epoch of local clock times, as the solver reads them.
 */
object LabourCode {
    /** Art. 132: at least 11 hours of uninterrupted rest in each 24 hours (doba pracownicza). */
    const val DAILY_REST_HOURS = LEGAL_MINIMUM_REST_HOURS

    /** Art. 133: at least 35 hours of uninterrupted rest in each week. */
    const val WEEKLY_REST_HOURS = 35

    /** Art. 129 §1: 8 hours a day; work beyond the person's daily norm is overtime (art. 151). */
    const val DAILY_NORM_HOURS = 8.0

    /** Art. 131 §1: at most 48 hours a week, overtime included, on average in the settlement period. */
    const val AVERAGE_WEEKLY_MAX_HOURS = 48.0

    /** Art. 151 §3: at most 150 hours of overtime a calendar year. */
    const val YEARLY_OVERTIME_HOURS = 150.0

    /**
     * The overtime one month may hold at the pace of the yearly limit (150 / 12 = 12.5 h). A roster
     * shows one month, so the yearly cap is checked as its monthly share.
     */
    const val MONTHLY_OVERTIME_HOURS = YEARLY_OVERTIME_HOURS / 12

    private const val DAY = 24 * 60L
    private const val WEEK = 7 * DAY

    /**
     * Whether the hours limits (daily norm, the 48-hour average, overtime) apply: they are the
     * labour code's, so only to an employment contract (umowa o pracę).
     */
    fun hoursRulesApply(contract: SystemContractType): Boolean = contract == SystemContractType.STANDARD_EMPLOYMENT

    /**
     * SH-133: whether the rest rules (art. 132's 11 hours, art. 133's 35 hours) are checked for
     * [contract]. The labour code covers only employees (art. 2): an employment contract always,
     * and an unknown one (the planner cannot read pay terms; payroll costs it as employment too).
     * Civil-law contracts, specific-task contracts and own businesses only when the workplace
     * applies them to everyone as its own policy ([restForAllContracts], the owner's rule 8; on by
     * default). The owner or a partner without a contract never.
     */
    fun restRulesApply(contract: SystemContractType, restForAllContracts: Boolean): Boolean = when (contract) {
        SystemContractType.STANDARD_EMPLOYMENT, SystemContractType.NOT_SET -> true
        SystemContractType.CIVIL_LAW_CONTRACT, SystemContractType.SPECIFIC_TASK_CONTRACT, SystemContractType.B2B_CONTRACTOR -> restForAllContracts
        SystemContractType.OWNER_PARTNER -> false
    }

    /**
     * SH-133: why a rule binds [contract]: the labour code for an employment contract (and an
     * unknown one, costed as employment), the company's own policy for everyone else.
     */
    fun basis(contract: SystemContractType): LegalBasis = when (contract) {
        SystemContractType.STANDARD_EMPLOYMENT, SystemContractType.NOT_SET -> LegalBasis.LABOUR_CODE
        SystemContractType.CIVIL_LAW_CONTRACT, SystemContractType.SPECIFIC_TASK_CONTRACT,
        SystemContractType.B2B_CONTRACTOR, SystemContractType.OWNER_PARTNER -> LegalBasis.COMPANY_POLICY
    }

    /** The longest uninterrupted time without work in [from, to), given the work [spans] (any order). */
    fun longestRestMinutes(spans: List<MinuteSpan>, from: Long, to: Long): Long {
        if (to <= from) return 0
        var longest = 0L
        var free = from
        spans.asSequence().filter { it.end > from && it.start < to }.sortedBy { it.start }.forEach { span ->
            if (span.start > free) longest = maxOf(longest, span.start - free)
            free = maxOf(free, span.end)
        }
        if (to > free) longest = maxOf(longest, to - free)
        return longest
    }

    /**
     * Art. 132 for the 24 hours from the start of [first]: the longest rest in them, given all of
     * the person's [spans]. Below [DAILY_REST_HOURS] the rule is broken (a later shift starts too
     * soon, or [first] itself is longer than 13 hours).
     */
    fun dailyRestMinutes(spans: List<MinuteSpan>, first: MinuteSpan): Long =
        longestRestMinutes(spans, first.start, first.start + DAY)

    /**
     * Art. 132 for a new span [added] among the person's [spans] (which must not contain it):
     * the shortest rest of the 24-hour periods it changes (those starting at it and at any of the
     * spans starting in the 24 hours before its end). Null when no period is affected.
     */
    fun dailyRestMinutesWith(spans: List<MinuteSpan>, added: MinuteSpan): Long? {
        val all = spans + added
        return all.filter { it.start < added.end && it.start + DAY > added.start }
            .minOfOrNull { dailyRestMinutes(all, it) }
    }

    /**
     * Epoch day of the first day of the week holding [epochDay]. For the labour code a week is 7
     * days in a row counted from the first day of the settlement period (art. 128 §3), here the
     * roster's month starting on [periodStartEpochDay]: the 1st, 8th, 15th, 22nd and 29th.
     */
    fun weekStartEpochDay(epochDay: Long, periodStartEpochDay: Long): Long =
        periodStartEpochDay + (epochDay - periodStartEpochDay).floorDiv(7L) * 7

    /**
     * Art. 133 for the week starting on [weekStartEpochDay]: the longest rest that starts in it
     * (one under way when the week starts counts from the week's start), up to the next work even
     * when that is after the week's end; [Long.MAX_VALUE] when no work follows. So a weekend off
     * across the weeks' boundary (Friday 16:00 to Monday 8:00) is one rest, not two short ones, and
     * a rest is never clipped by the end of the roster.
     */
    fun weeklyRestMinutes(spans: List<MinuteSpan>, weekStartEpochDay: Long): Long {
        val from = weekStartEpochDay * DAY
        val to = from + WEEK
        var longest = 0L
        var free = from
        for (span in spans.asSequence().filter { it.end > from }.sortedBy { it.start }) {
            if (free >= to) break
            if (span.start > free) longest = maxOf(longest, span.start - free)
            free = maxOf(free, span.end)
        }
        return if (free < to) Long.MAX_VALUE else longest
    }

    /**
     * The weeks (their first epoch days) of the period starting on [periodStartEpochDay] whose
     * weekly rest [span] can shorten: those it falls in, and the one before (its rest may run
     * into the span), never before the period.
     */
    fun weeksOf(span: MinuteSpan, periodStartEpochDay: Long): List<Long> {
        val first = maxOf(periodStartEpochDay, weekStartEpochDay(span.start.floorDiv(DAY), periodStartEpochDay) - 7)
        val last = weekStartEpochDay((span.end - 1).floorDiv(DAY), periodStartEpochDay)
        return generateSequence(first) { it + 7 }.takeWhile { it <= last }.toList()
    }

    /** Art. 131: the most hours a settlement period of [days] days may hold (48 a week on average). */
    fun periodMaxHours(days: Int): Double = AVERAGE_WEEKLY_MAX_HOURS * days / 7.0

    /**
     * Art. 151 overtime of a period: work beyond the daily norm on each day, plus what is left
     * above the period's norm ([periodNormMinutes]) once those are taken out.
     */
    fun overtimeMinutes(minutesPerDay: Collection<Int>, dailyNormMinutes: Double, periodNormMinutes: Double): Double {
        val daily = minutesPerDay.sumOf { maxOf(0.0, it - dailyNormMinutes) }
        val total = minutesPerDay.sum().toDouble()
        return daily + maxOf(0.0, total - daily - periodNormMinutes)
    }
}

/** A span of work in minutes since the epoch (local clock), end exclusive. */
data class MinuteSpan(val start: Long, val end: Long)

/** SH-133: what makes a warning binding. */
enum class LegalBasis {
    /** Kodeks pracy: a legal requirement for an employee (umowa o pracę). */
    LABOUR_CODE,
    /** The workplace's own rule: the law does not require it for this contract. */
    COMPANY_POLICY
}
